// "Install YADAHub" support. Chromium browsers (Android Chrome, Edge, Samsung Internet) fire
// `beforeinstallprompt` once the manifest makes the site installable; it can fire before React
// mounts, so it is captured here at import time and handed to the banner later. iOS Safari has
// no such event: the banner shows Share → Add to Home Screen instructions instead.

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'yada.installBanner.dismissedAt';
const DISMISS_DAYS = 30;

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // we show our own banner instead of the browser's mini-infobar
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  // Once installed the browser stops firing beforeinstallprompt, so clearing it is enough.
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export function subscribeInstall(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function canPromptInstall(): boolean {
  return !!deferred;
}

/** Shows the browser's install dialog. Resolves true when the user accepted. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null; // the event can only be used once
  notify();
  await e.prompt();
  const { outcome } = await e.userChoice;
  return outcome === 'accepted';
}

/** Already opened from the home screen (or as an installed desktop app). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIOS(ua = navigator.userAgent, platform = navigator.platform, touch = navigator.maxTouchPoints): boolean {
  // iPadOS 13+ reports itself as a Mac; the touch screen gives it away.
  return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && touch > 1);
}

/** Phones and tablets only: on desktop the browser's own address-bar install button is enough. */
export function isHandheld(): boolean {
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

export function wasDismissedRecently(now = Date.now()): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return !!at && now - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function rememberDismissed(now = Date.now()): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(now));
  } catch {
    /* private mode: the banner just comes back next visit */
  }
}
